/**
 * B2 · CHAT DEL AULA — selectores PUROS (testeables sin base de datos).
 *
 * Aquí vive lo que la interfaz necesita calcular y NO es autorización:
 * agrupación por día, no-leídos contra la marca propia, agregado de reacciones
 * y «visto por última vez» derivado.
 *
 * La presencia (§7a del plan) se DERIVA de lo que ya está sincronizado: el
 * último mensaje de cada persona y su marca de leído. No hay latido, ni tabla,
 * ni escritura periódica — 300 alumnos escribiendo cada 30 s serían ~15.000
 * entregas de fila por minuto por un dato que caduca en medio minuto.
 */

export interface ChatMessageLike {
  id: string;
  author_id?: string | null;
  created_at?: string | null;
  hidden_at?: string | null;
  pinned_at?: string | null;
}

export interface ChatReactionLike {
  message_id?: string | null;
  user_id?: string | null;
  emoji?: string | null;
}

/** Un día de conversación: la clave ISO (`YYYY-MM-DD`) y sus mensajes. */
export interface ChatDayGroup<T extends ChatMessageLike> {
  day: string;
  messages: T[];
}

/**
 * Agrupa los mensajes por día LOCAL conservando el orden cronológico.
 *
 * El corte es por día local y no por UTC a propósito: la cabecera «hoy» de un
 * chat tiene que coincidir con el calendario de quien lee, no con Greenwich.
 */
export function groupMessagesByDay<T extends ChatMessageLike>(
  messages: readonly T[],
): ChatDayGroup<T>[] {
  const out: ChatDayGroup<T>[] = [];
  for (const m of messages) {
    const day = localDay(m.created_at);
    const last = out.at(-1);
    if (last && last.day === day) {
      last.messages.push(m);
      continue;
    }
    out.push({ day, messages: [m] });
  }
  return out;
}

/** Clave `YYYY-MM-DD` en zona local; cadena vacía si no hay fecha usable. */
function localDay(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const mm = `${d.getMonth() + 1}`.padStart(2, "0");
  const dd = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Mensajes sin leer para una persona: los POSTERIORES a su marca de leído y
 * escritos por OTROS.
 *
 * Sin marca (`lastReadAt` nulo) cuenta todo lo ajeno: es la primera visita al
 * aula, y decir «0 sin leer» ahí escondería la conversación entera.
 */
export function countUnreadMessages(
  messages: readonly ChatMessageLike[],
  userId: string,
  lastReadAt: string | null | undefined,
): number {
  const since = lastReadAt ? Date.parse(lastReadAt) : Number.NEGATIVE_INFINITY;
  let unread = 0;
  for (const m of messages) {
    if (m.author_id === userId) {
      continue;
    }
    const at = m.created_at ? Date.parse(m.created_at) : Number.NaN;
    if (!Number.isNaN(at) && at > since) {
      unread += 1;
    }
  }
  return unread;
}

/** Recuento de reacciones de UN mensaje: emoji → nº, y cuál puse yo. */
export interface ReactionSummary {
  counts: { emoji: string; count: number }[];
  mine: string | null;
}

/**
 * Agrega las reacciones de un mensaje. El orden de salida es por número
 * descendente y, a igualdad, alfabético: sin el desempate, dos dispositivos
 * pintarían los mismos emojis en distinto orden y la fila «bailaría» al
 * sincronizar.
 */
export function summarizeReactions(
  reactions: readonly ChatReactionLike[],
  messageId: string,
  userId: string,
): ReactionSummary {
  const counts = new Map<string, number>();
  let mine: string | null = null;
  for (const r of reactions) {
    if (r.message_id !== messageId || !r.emoji) {
      continue;
    }
    counts.set(r.emoji, (counts.get(r.emoji) ?? 0) + 1);
    if (r.user_id === userId) {
      mine = r.emoji;
    }
  }
  return {
    counts: [...counts.entries()]
      .map(([emoji, count]) => ({ emoji, count }))
      .toSorted((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji)),
    mine,
  };
}

/**
 * §7a · PRESENCIA DERIVADA, coste cero: última actividad conocida de cada
 * persona del aula = su mensaje más reciente. Sin latidos ni escrituras
 * periódicas; cubre la sensación de «el aula está viva» sin un canal en vivo.
 */
export function lastSeenByAuthor(messages: readonly ChatMessageLike[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const m of messages) {
    if (!m.author_id || !m.created_at) {
      continue;
    }
    const prev = out.get(m.author_id);
    if (!prev || Date.parse(m.created_at) > Date.parse(prev)) {
      out.set(m.author_id, m.created_at);
    }
  }
  return out;
}
